"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Icon from "@/components/ui/AppIcon";
import { createClient } from "@/lib/supabase/client";

interface Advertiser {
  id: string;
  brand: string;
  industry: string;
  streamingServices: string[];
  adType: string;
  reportCount: number;
  lastSeen: string;
  status: "confirmed" | "reported" | "verified";
  upvotes: number;
  downvotes: number;
}

interface VoteState {
  upvotes: number;
  downvotes: number;
  userVote: "up" | "down" | null;
}

const COMMUNITY_VERIFIED_THRESHOLD = 50;
const STREAMING_SERVICES = ["All", "Netflix", "Hulu", "Disney+", "Peacock", "Paramount+", "Max"];
const INDUSTRIES = ["All Industries", "Insurance", "Retail", "Food & Beverage", "Finance", "Healthcare", "Technology", "Automotive", "Travel", "Fashion"];

const VOTE_STORAGE_KEY = "pmp_blacklist_votes";

function loadStoredVotes(): Record<string, "up" | "down"> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(VOTE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStoredVotes(votes: Record<string, "up" | "down">) {
  try {
    localStorage.setItem(VOTE_STORAGE_KEY, JSON.stringify(votes));
  } catch {}
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function getStatusFromVotesAndReports(upvotes: number, reportCount: number): "confirmed" | "reported" | "verified" {
  if (upvotes >= COMMUNITY_VERIFIED_THRESHOLD || reportCount >= 4000) return "verified";
  if (reportCount >= 2000) return "confirmed";
  return "reported";
}

function StatusBadge({ status }: { status: Advertiser["status"] }) {
  const config = {
    verified: { label: "Verified", bg: "bg-[#FF2222]", text: "text-white" },
    confirmed: { label: "Confirmed", bg: "bg-amber-500", text: "text-white" },
    reported: { label: "Reported", bg: "bg-gray-200", text: "text-gray-700" },
  };
  const c = config[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

function CommunityVerifiedBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
      </svg>
      Community Verified
    </span>
  );
}

function VoteButtons({
  advertiserId,
  votes,
  onVote,
  disabled,
}: {
  advertiserId: string;
  votes: VoteState;
  onVote: (id: string, direction: "up" | "down") => void;
  disabled?: boolean;
}) {
  const netVotes = votes.upvotes - votes.downvotes;
  const isCommunityVerified = votes.upvotes >= COMMUNITY_VERIFIED_THRESHOLD;

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        onClick={() => onVote(advertiserId, "up")}
        aria-label="Upvote this advertiser"
        aria-pressed={votes.userVote === "up"}
        disabled={disabled}
        className={`flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          votes.userVote === "up" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-white/5 text-muted border border-white/10 hover:border-emerald-500/50 hover:text-emerald-400"
        }`}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
          <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
        </svg>
      </button>
      <span
        className={`text-[11px] font-bold tabular-nums leading-none ${netVotes > 0 ? "text-emerald-400" : netVotes < 0 ? "text-[#FF2222]" : "text-muted"}`}
        aria-label={`${netVotes} net votes`}
      >
        {netVotes > 0 ? `+${netVotes}` : netVotes}
      </span>
      <button
        onClick={() => onVote(advertiserId, "down")}
        aria-label="Downvote this advertiser"
        aria-pressed={votes.userVote === "down"}
        disabled={disabled}
        className={`flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          votes.userVote === "down" ? "bg-[#FF2222]/20 text-[#FF2222] border border-[#FF2222]/40" : "bg-white/5 text-muted border border-white/10 hover:border-[#FF2222]/50 hover:text-[#FF2222]"
        }`}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
          <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
        </svg>
      </button>
      {isCommunityVerified && (
        <div className="mt-1">
          <CommunityVerifiedBadge />
        </div>
      )}
    </div>
  );
}

export default function AdvertiserBlacklist() {
  const supabase = createClient();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [search, setSearch] = useState("");
  const [activeService, setActiveService] = useState("All");
  const [activeIndustry, setActiveIndustry] = useState("All Industries");
  const [sortBy, setSortBy] = useState<"reports" | "recent">("reports");
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ brand: "", service: "", industry: "", adType: "", notes: "" });
  const [voteMap, setVoteMap] = useState<Record<string, VoteState>>({});
  const [votingIds, setVotingIds] = useState<Set<string>>(new Set());

  const fetchBlacklist = useCallback(async () => {
    setLoadingData(true);
    const { data } = await supabase
      .from("advertiser_blacklist")
      .select("*")
      .eq("blacklist_status", "approved")
      .order("report_count", { ascending: false });

    if (data) {
      const storedVotes = loadStoredVotes();
      const mapped: Advertiser[] = data.map((row) => ({
        id: row.id,
        brand: row.brand,
        industry: row.industry,
        streamingServices: row.streaming_services ?? [],
        adType: row.ad_type,
        reportCount: row.report_count,
        lastSeen: row.last_seen,
        status: getStatusFromVotesAndReports(row.upvotes, row.report_count),
        upvotes: row.upvotes,
        downvotes: row.downvotes,
      }));
      setAdvertisers(mapped);
      const initialVotes: Record<string, VoteState> = {};
      mapped.forEach((a) => {
        initialVotes[a.id] = {
          upvotes: a.upvotes,
          downvotes: a.downvotes,
          userVote: storedVotes[a.id] ?? null,
        };
      });
      setVoteMap(initialVotes);
    }
    setLoadingData(false);
  }, []);

  useEffect(() => {
    fetchBlacklist();
  }, [fetchBlacklist]);

  async function handleVote(id: string, direction: "up" | "down") {
    if (votingIds.has(id)) return;

    const current = voteMap[id];
    if (!current) return;

    // Determine new vote state
    const isSameDirection = current.userVote === direction;
    const newDirection: "up" | "down" | "none" = isSameDirection ? "none" : direction;
    const previousDirection: "up" | "down" | "none" = current.userVote ?? "none";

    // Optimistic update
    setVoteMap((prev) => {
      const c = prev[id];
      if (!c) return prev;
      if (isSameDirection) {
        return {
          ...prev,
          [id]: {
            upvotes: direction === "up" ? c.upvotes - 1 : c.upvotes,
            downvotes: direction === "down" ? c.downvotes - 1 : c.downvotes,
            userVote: null,
          },
        };
      }
      return {
        ...prev,
        [id]: {
          upvotes: direction === "up" ? c.upvotes + 1 : c.userVote === "up" ? c.upvotes - 1 : c.upvotes,
          downvotes: direction === "down" ? c.downvotes + 1 : c.userVote === "down" ? c.downvotes - 1 : c.downvotes,
          userVote: direction,
        },
      };
    });

    // Persist to localStorage
    const storedVotes = loadStoredVotes();
    if (newDirection === "none") {
      delete storedVotes[id];
    } else {
      storedVotes[id] = newDirection;
    }
    saveStoredVotes(storedVotes);

    // Persist to Supabase via RPC
    setVotingIds((prev) => new Set(prev).add(id));
    try {
      const { data, error } = await supabase.rpc("cast_blacklist_vote", {
        p_advertiser_id: id,
        p_direction: newDirection,
        p_previous: previousDirection,
      });

      if (!error && data && data.length > 0) {
        // Sync with server-confirmed counts
        setVoteMap((prev) => ({
          ...prev,
          [id]: {
            upvotes: data[0].upvotes,
            downvotes: data[0].downvotes,
            userVote: newDirection === "none" ? null : newDirection,
          },
        }));
      }
    } catch {
      // Keep optimistic state on network error
    } finally {
      setVotingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".blacklist-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.remove("opacity-0-init");
                card.classList.add("animate-fade-up");
              }, i * 50);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [advertisers]);

  const filtered = advertisers
    .filter((a) => {
      const matchSearch = search === "" || a.brand.toLowerCase().includes(search.toLowerCase()) || a.industry.toLowerCase().includes(search.toLowerCase());
      const matchService = activeService === "All" || a.streamingServices.includes(activeService);
      const matchIndustry = activeIndustry === "All Industries" || a.industry === activeIndustry;
      return matchSearch && matchService && matchIndustry;
    })
    .sort((a, b) => sortBy === "reports" ? b.reportCount - a.reportCount : 0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.from("advertiser_blacklist").insert({
      brand: formData.brand,
      industry: formData.industry || "Other",
      streaming_services: formData.service ? [formData.service] : [],
      ad_type: formData.adType || "Pre-roll 30s",
      notes: formData.notes || null,
      blacklist_status: "pending",
      report_count: 1,
      last_seen: "Today",
    });
    setSubmitting(false);
    if (!error) {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setShowForm(false);
        setFormData({ brand: "", service: "", industry: "", adType: "", notes: "" });
      }, 3000);
    }
  }

  const totalReports = advertisers.reduce((sum, a) => sum + a.reportCount, 0);
  const uniqueServices = new Set(advertisers.flatMap((a) => a.streamingServices)).size;

  return (
    <section
      id="blacklist"
      ref={sectionRef}
      className="bg-ink py-0 overflow-hidden"
      aria-label="Advertiser blacklist"
    >
      {/* ── Dramatic intro banner ── */}
      <div className="relative bg-[#0A0A0A] border-y-2 border-[#FF2222] py-16 md:py-24 px-6 md:px-12 overflow-hidden">
        {/* Red glow blobs */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FF2222]/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#FF2222]/10 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-32 bg-[#FF2222]/5 blur-2xl" />
        </div>
        {/* Diagonal stripe texture */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #FF2222 0, #FF2222 1px, transparent 0, transparent 50%)", backgroundSize: "12px 12px" }}
          aria-hidden="true"
        />
        <div className="relative max-w-8xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FF2222] text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse inline-block" aria-hidden="true" />
              Live Accountability Database
            </div>
            <h2 className="font-display text-6xl md:text-8xl lg:text-9xl font-black text-white leading-[0.9] tracking-tightest">
              The<br />
              <span className="text-[#FF2222]">Blacklist.</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl text-white/60 font-light max-w-lg leading-relaxed">
              Every brand that paid to interrupt your paid subscription. <strong className="text-white font-semibold">Don&apos;t buy from them.</strong>
            </p>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            <div className="grid grid-cols-3 md:grid-cols-1 gap-3 md:gap-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-center md:text-right">
                <div className="stat-number text-4xl md:text-5xl font-black text-[#FF2222]">{advertisers.length}</div>
                <div className="text-xs text-white/40 uppercase tracking-widest font-medium mt-1">Brands Blacklisted</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-center md:text-right">
                <div className="stat-number text-4xl md:text-5xl font-black text-[#FF2222]">{uniqueServices}</div>
                <div className="text-xs text-white/40 uppercase tracking-widest font-medium mt-1">Services Caught</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-center md:text-right">
                <div className="stat-number text-4xl md:text-5xl font-black text-[#FF2222]">{formatCount(totalReports)}</div>
                <div className="text-xs text-white/40 uppercase tracking-widest font-medium mt-1">Total Reports</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="px-6 md:px-12 py-16 md:py-20">
      <div className="max-w-8xl mx-auto">

        {/* ── Legend ── */}
        <div className="mb-10 flex flex-wrap items-center gap-4">
          <span className="text-xs font-bold uppercase tracking-widest text-muted">Status key:</span>
          <span className="flex items-center gap-1.5 text-sm text-surface/60">
            <span className="w-2 h-2 rounded-full bg-[#FF2222] inline-block"></span>
            Verified
          </span>
          <span className="flex items-center gap-1.5 text-sm text-surface/60">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
            Confirmed
          </span>
          <span className="flex items-center gap-1.5 text-sm text-surface/60">
            <span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>
            Reported
          </span>
          <span className="flex items-center gap-1.5 text-sm text-surface/60">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            Community Verified
          </span>
        </div>

        {/* ── Filters & Search ── */}
        <div className="mb-8 space-y-4">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={18} variant="outline" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Search brands or industries…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-surface placeholder-muted text-sm focus:outline-none focus:border-[#FF2222] transition-colors"
              aria-label="Search advertisers"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STREAMING_SERVICES.map((s) => (
              <button
                key={s}
                onClick={() => setActiveService(s)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                  activeService === s ? "bg-[#FF2222] text-white" : "bg-white/5 text-muted border border-white/10 hover:border-[#FF2222] hover:text-surface"
                }`}
                aria-pressed={activeService === s}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <select
              value={activeIndustry}
              onChange={(e) => setActiveIndustry(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-surface focus:outline-none focus:border-[#FF2222] transition-colors cursor-pointer"
              aria-label="Filter by industry"
            >
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind} className="bg-[#1C1C1C]">{ind}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className="uppercase tracking-wider font-medium">Sort:</span>
              <button onClick={() => setSortBy("reports")} className={`px-3 py-1.5 rounded-lg transition-all ${sortBy === "reports" ? "bg-[#FF2222] text-white" : "bg-white/5 hover:bg-white/10 text-muted"}`}>Most Reported</button>
              <button onClick={() => setSortBy("recent")} className={`px-3 py-1.5 rounded-lg transition-all ${sortBy === "recent" ? "bg-[#FF2222] text-white" : "bg-white/5 hover:bg-white/10 text-muted"}`}>Recent</button>
            </div>
          </div>
        </div>

        {/* ── Blacklist Table ── */}
        <div className="rounded-2xl border border-white/10 overflow-hidden mb-10">
          <div className="hidden md:grid grid-cols-[2fr_1fr_1.5fr_1fr_1fr_1fr_80px] gap-4 px-6 py-3 bg-white/5 border-b border-white/10 text-xs font-bold uppercase tracking-widest text-muted">
            <span>Brand</span>
            <span>Industry</span>
            <span>Streaming Service(s)</span>
            <span>Ad Type</span>
            <span>Reports</span>
            <span>Status</span>
            <span className="text-center">Votes</span>
          </div>

          {loadingData ? (
            <div className="py-16 text-center">
              <svg className="animate-spin w-6 h-6 text-[#FF2222] mx-auto" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-muted text-sm mt-3">Loading blacklist…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-muted text-sm">No advertisers match your filters.</div>
          ) : (
            filtered.map((advertiser, i) => {
              const votes = voteMap[advertiser.id] ?? { upvotes: advertiser.upvotes, downvotes: advertiser.downvotes, userVote: null };
              const isCommunityVerified = votes.upvotes >= COMMUNITY_VERIFIED_THRESHOLD;
              return (
                <div
                  key={advertiser.id}
                  className={`blacklist-card opacity-0-init grid grid-cols-1 md:grid-cols-[2fr_1fr_1.5fr_1fr_1fr_1fr_80px] gap-3 md:gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/5 transition-colors duration-200 ${i % 2 === 0 ? "" : "bg-white/[0.02]"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#FF2222]/10 border border-[#FF2222]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#FF2222] font-black text-sm font-display">{advertiser.brand.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-surface font-semibold text-sm">{advertiser.brand}</div>
                      <div className="text-muted text-xs md:hidden">{advertiser.industry}</div>
                      {isCommunityVerified && <div className="mt-1 md:hidden"><CommunityVerifiedBadge /></div>}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center text-sm text-muted">{advertiser.industry}</div>
                  <div className="flex items-center flex-wrap gap-1.5">
                    {advertiser.streamingServices?.map((s) => (
                      <span key={s} className="px-2 py-0.5 rounded-md bg-white/10 text-surface/70 text-[11px] font-medium">{s}</span>
                    ))}
                  </div>
                  <div className="flex items-center text-xs text-muted">{advertiser.adType}</div>
                  <div className="flex items-center gap-1.5">
                    <Icon name="FlagIcon" size={13} variant="solid" className="text-[#FF2222]" />
                    <span className="text-surface font-bold text-sm">{formatCount(advertiser.reportCount)}</span>
                    <span className="text-muted text-xs hidden lg:inline">reports</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={advertiser.status} />
                    <span className="text-[11px] text-muted">{advertiser.lastSeen}</span>
                    {isCommunityVerified && <div className="hidden md:block mt-0.5"><CommunityVerifiedBadge /></div>}
                  </div>
                  <div className="flex md:justify-center items-start md:items-center">
                    <VoteButtons advertiserId={advertiser.id} votes={votes} onVote={handleVote} disabled={votingIds.has(advertiser.id)} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Report a New Advertiser ── */}
        <div className="rounded-2xl border border-[#FF2222]/30 bg-[#FF2222]/5 p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-black text-surface leading-tight tracking-tightest mb-2">
                Saw an ad on a paid tier?
              </h3>
              <p className="text-muted text-sm font-light">Report the brand. We verify and add it to the blacklist. Every report counts.</p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#FF2222] text-white px-7 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#CC0000] hover:scale-105 transition-all duration-300"
              aria-expanded={showForm}
            >
              <Icon name="PlusIcon" size={16} variant="outline" />
              Report an Advertiser
            </button>
          </div>

          {showForm && (
            <div className="border-t border-white/10 pt-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#FF2222]/20 flex items-center justify-center mx-auto mb-4 animate-check-pop">
                    <Icon name="CheckIcon" size={32} variant="outline" className="text-[#FF2222]" />
                  </div>
                  <h4 className="text-surface font-bold text-xl mb-2">Report Submitted</h4>
                  <p className="text-muted text-sm">Thank you. Our team will verify and add it to the blacklist within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5" aria-label="Report advertiser form">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted mb-2" htmlFor="brand-name">Brand / Company Name *</label>
                    <input id="brand-name" type="text" required placeholder="e.g. AutoDrive Insurance" value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-surface placeholder-muted text-sm focus:outline-none focus:border-[#FF2222] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted mb-2" htmlFor="streaming-service">Streaming Service *</label>
                    <select id="streaming-service" required value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-surface text-sm focus:outline-none focus:border-[#FF2222] transition-colors cursor-pointer">
                      <option value="" className="bg-[#1C1C1C]">Select service…</option>
                      {STREAMING_SERVICES.filter((s) => s !== "All").map((s) => (<option key={s} value={s} className="bg-[#1C1C1C]">{s}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted mb-2" htmlFor="industry">Industry</label>
                    <select id="industry" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-surface text-sm focus:outline-none focus:border-[#FF2222] transition-colors cursor-pointer">
                      <option value="" className="bg-[#1C1C1C]">Select industry…</option>
                      {INDUSTRIES.filter((i) => i !== "All Industries").map((ind) => (<option key={ind} value={ind} className="bg-[#1C1C1C]">{ind}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted mb-2" htmlFor="ad-type">Ad Type</label>
                    <select id="ad-type" value={formData.adType} onChange={(e) => setFormData({ ...formData, adType: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-surface text-sm focus:outline-none focus:border-[#FF2222] transition-colors cursor-pointer">
                      <option value="" className="bg-[#1C1C1C]">Select type…</option>
                      {["Pre-roll 15s","Pre-roll 30s","Pre-roll 60s","Mid-roll 15s","Mid-roll 30s","Banner / Overlay"].map((t) => (<option key={t} value={t} className="bg-[#1C1C1C]">{t}</option>))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted mb-2" htmlFor="notes">Additional Notes</label>
                    <textarea id="notes" rows={3} placeholder="Describe the ad, when you saw it, any other details…" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-surface placeholder-muted text-sm focus:outline-none focus:border-[#FF2222] transition-colors resize-none" />
                  </div>
                  <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#FF2222] text-white px-8 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#CC0000] hover:scale-105 transition-all duration-300 disabled:opacity-60">
                      <Icon name="FlagIcon" size={15} variant="solid" />
                      {submitting ? "Submitting…" : "Submit Report"}
                    </button>
                    <p className="text-xs text-muted font-light">Reports are reviewed within 24 hours. Verified reports are added to the public blacklist.</p>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
          <p className="text-sm text-muted font-light">Share the blacklist — make advertisers think twice before buying ads on paid tiers.</p>
          <a
            href="https://twitter.com/intent/tweet?text=These%20brands%20are%20paying%20to%20interrupt%20your%20PAID%20streaming%20subscription.%20See%20the%20full%20blacklist%20at%20PaidMeansPaid.com%20%23PaidMeansPaid"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-2 border-2 border-[#FF2222] text-[#FF2222] px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#FF2222] hover:text-white transition-all duration-300"
            aria-label="Share the advertiser blacklist on Twitter"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Share the Blacklist
          </a>
        </div>

      </div>
      </div>
    </section>
  );
}
